import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/pt-br";

import type { DateValue } from "react-aria-components";
import type {
	ConsolidatedPositionHistoryItem,
	PositionHistoryItem,
	Telemetry,
	Trip,
	TripRouteData,
} from "../types";

dayjs.locale("pt-br");
dayjs.extend(relativeTime);
dayjs.extend(utc);

const MINUTES_IN_HOUR = 60;
const MAX_HOUR_VALUE = 24;
const MIN_INTERVAL_MINUTES = 5;
const DEFAULT_END_HOUR = 23.99;
const PAGINATION_MAX_VISIBLE = 4;
/** Brazil does not observe DST since 2019, so a fixed UTC-3 offset is safe here. */
const BRAZIL_UTC_OFFSET_MINUTES = -180;

const parseHoursAndMinutesFromDecimal = (
	decimalHours: number
): { hours: number; minutes: number } => {
	const hours = Math.floor(decimalHours);
	const minutes =
		Math.round(
			((decimalHours - hours) * MINUTES_IN_HOUR) / MIN_INTERVAL_MINUTES
		) * MIN_INTERVAL_MINUTES;
	return { hours, minutes };
};

const formatNumberToTwoDigits = (num: number): string =>
	String(num).padStart(2, "0");

export const formatDate = (dateString: string): string => {
	const date = dayjs(dateString);
	const now = dayjs();

	if (now.diff(date, "minute") < 1) {
		return "agora";
	}

	if (now.diff(date, "minute") < MINUTES_IN_HOUR) {
		return date.fromNow();
	}

	if (now.diff(date, "hour") < 24) {
		return date.fromNow();
	}

	return date.format("DD/MM/YY HH:mm");
};

const POSITION_COORDINATE_PRECISION = 5;
const POSITION_GROUP_GAP_MS = 3 * 60 * 1000;

const normalizePositionCoordinate = (value: string): string => {
	const numericValue = Number.parseFloat(value.replace(",", "."));
	if (Number.isNaN(numericValue)) {
		return value.trim();
	}
	return numericValue.toFixed(POSITION_COORDINATE_PRECISION);
};

export const getPaginationPages = ({
	currentPage,
	totalPages,
}: {
	currentPage: number;
	totalPages: number;
}): number[] => {
	const half = Math.floor(PAGINATION_MAX_VISIBLE / 2);
	let start = Math.max(1, currentPage - half);
	let end = start + PAGINATION_MAX_VISIBLE - 1;

	if (end > totalPages) {
		end = totalPages;
		start = Math.max(1, end - PAGINATION_MAX_VISIBLE + 1);
	}

	return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

export const consolidatePositionHistory = (
	items: PositionHistoryItem[]
): ConsolidatedPositionHistoryItem[] => {
	if (items.length === 0) {
		return [];
	}

	const sortedItems = [...items].sort(
		(a, b) =>
			new Date(a.dateTelemetry).getTime() - new Date(b.dateTelemetry).getTime()
	);

	const groups: ConsolidatedPositionHistoryItem[] = [];

	for (const item of sortedItems) {
		const normalizedLat = normalizePositionCoordinate(item.latitude);
		const normalizedLng = normalizePositionCoordinate(item.longitude);
		const currentTime = new Date(item.dateTelemetry).getTime();
		const lastGroup = groups.at(-1);
		const lastItem = lastGroup?.items.at(-1);

		const isSamePosition =
			lastGroup &&
			normalizePositionCoordinate(lastGroup.latitude) === normalizedLat &&
			normalizePositionCoordinate(lastGroup.longitude) === normalizedLng;

		const isWithinGroupingWindow =
			lastItem &&
			currentTime - new Date(lastItem.dateTelemetry).getTime() <=
				POSITION_GROUP_GAP_MS;

		if (lastGroup && isSamePosition && isWithinGroupingWindow) {
			lastGroup.items.push(item);
			lastGroup.endDateTelemetry = item.dateTelemetry;
			lastGroup.speed = item.speed;
			lastGroup.driver = item.driver;
			lastGroup.address = item.address ?? lastGroup.address;
			continue;
		}

		groups.push({
			id: String(item.id),
			startDateTelemetry: item.dateTelemetry,
			endDateTelemetry: item.dateTelemetry,
			latitude: item.latitude,
			longitude: item.longitude,
			address: item.address,
			speed: item.speed,
			driver: item.driver,
			items: [item],
		});
	}

	return groups;
};

export const roundToFiveMinutes = (decimalHours: number): number => {
	if (decimalHours >= MAX_HOUR_VALUE) {
		return DEFAULT_END_HOUR;
	}

	const { hours, minutes } = parseHoursAndMinutesFromDecimal(decimalHours);

	if (minutes >= MINUTES_IN_HOUR) {
		return Math.min(hours + 1, DEFAULT_END_HOUR);
	}

	return hours + minutes / MINUTES_IN_HOUR;
};

export const formatHour = (decimalHours: number): string => {
	if (decimalHours >= MAX_HOUR_VALUE || decimalHours >= DEFAULT_END_HOUR) {
		return `${formatNumberToTwoDigits(23)}:${formatNumberToTwoDigits(59)}`;
	}

	const { hours, minutes } = parseHoursAndMinutesFromDecimal(decimalHours);

	if (minutes >= MINUTES_IN_HOUR) {
		return `${formatNumberToTwoDigits(hours + 1)}:00`;
	}

	return `${formatNumberToTwoDigits(hours)}:${formatNumberToTwoDigits(minutes)}`;
};

export const formatDateToApi = (
	dateValue: DateValue | null,
	isEndOfDay = false
): string | undefined => {
	if (!dateValue) {
		return;
	}

	const date = dayjs()
		.year(dateValue.year)
		.month(dateValue.month - 1)
		.date(dateValue.day);

	const time = isEndOfDay ? "23:59" : "00:00";
	return `${date.format("YYYY-MM-DD")} ${time}`;
};

export const formatDateWithTime = (
	dateValue: DateValue | null,
	hourValue: number
): string | undefined => {
	if (!dateValue) {
		return;
	}

	const date = dayjs()
		.year(dateValue.year)
		.month(dateValue.month - 1)
		.date(dateValue.day);

	const time = hourValue >= MAX_HOUR_VALUE ? "23:59" : formatHour(hourValue);

	return `${date.format("YYYY-MM-DD")} ${time}`;
};

export const formatDateWithTimeIso = (
	dateValue: DateValue | null,
	hourValue: number
): string | undefined => {
	if (!dateValue) {
		return;
	}

	const date = dayjs()
		.year(dateValue.year)
		.month(dateValue.month - 1)
		.date(dateValue.day);

	const time =
		hourValue >= MAX_HOUR_VALUE ? "23:59:59" : `${formatHour(hourValue)}:00`;

	return `${date.format("YYYY-MM-DD")}T${time}`;
};

export const formatDurationMinutes = (
	minutes: number | null | undefined
): string => {
	if (!minutes || minutes <= 0) {
		return "0min";
	}

	const totalMinutes = Math.round(minutes);
	const hours = Math.floor(totalMinutes / MINUTES_IN_HOUR);
	const remainingMinutes = totalMinutes % MINUTES_IN_HOUR;

	if (hours <= 0) {
		return `${remainingMinutes}min`;
	}

	return `${hours}h ${String(remainingMinutes).padStart(2, "0")}min`;
};

export const splitDurationMinutes = (
	minutes: number | null | undefined
): { value: string; unit: string } => {
	if (!minutes || minutes <= 0) {
		return { value: "0", unit: "min" };
	}

	const totalMinutes = Math.round(minutes);
	const hours = Math.floor(totalMinutes / MINUTES_IN_HOUR);
	const remainingMinutes = totalMinutes % MINUTES_IN_HOUR;

	if (hours <= 0) {
		return { value: String(remainingMinutes), unit: "min" };
	}

	return {
		value: String(hours),
		unit: `h ${String(remainingMinutes).padStart(2, "0")}min`,
	};
};

export const adjustTimeRangeForMinInterval = (
	timeRange: readonly [number, number],
	minMinutes = MIN_INTERVAL_MINUTES
): [number, number] => {
	const minIntervalHours = minMinutes / MINUTES_IN_HOUR;
	const [startHour, endHour] = timeRange;

	if (endHour - startHour >= minIntervalHours) {
		return [startHour, endHour];
	}

	if (endHour >= MAX_HOUR_VALUE - minIntervalHours) {
		const newStart = Math.max(
			0,
			roundToFiveMinutes(endHour - minIntervalHours)
		);
		return [newStart, endHour];
	}

	const newEnd = roundToFiveMinutes(startHour + minIntervalHours);
	return [startHour, newEnd];
};

export const convertCoordinate = (
	coordinate: string | undefined | null
): number => {
	if (!coordinate || coordinate.trim() === "") {
		return 0;
	}

	const normalized = coordinate.replace(",", ".");
	const parsed = Number(normalized);

	return Number.isNaN(parsed) ? 0 : parsed;
};

export const mapTripToRouteData = (
	trip: Trip,
	parseCoordinate: (value: string | undefined | null) => number
): TripRouteData => {
	const startLat = parseCoordinate(trip.startLatitude);
	const startLng = parseCoordinate(trip.startLongitude);
	const endLat = parseCoordinate(trip.endLatitude);
	const endLng = parseCoordinate(trip.endLongitude);
	const isTripOngoing = !trip.end;

	return {
		coordinates: [
			{ lat: startLat, lng: startLng },
			{ lat: endLat, lng: endLng },
		],
		startMarker: {
			lat: startLat,
			lng: startLng,
			address: trip.addressStarting,
			type: "start",
			showCar: isTripOngoing,
		},
		endMarker: isTripOngoing
			? undefined
			: {
					lat: endLat,
					lng: endLng,
					address: trip.addressFinal,
					type: "end",
				},
	};
};

export const haversineDistance = (
	lat1: number,
	lng1: number,
	lat2: number,
	lng2: number
): number => {
	const earthRadiusKm = 6371;
	const dLat = ((lat2 - lat1) * Math.PI) / 180;
	const dLng = ((lng2 - lng1) * Math.PI) / 180;
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((lat1 * Math.PI) / 180) *
			Math.cos((lat2 * Math.PI) / 180) *
			Math.sin(dLng / 2) *
			Math.sin(dLng / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return earthRadiusKm * c;
};

export const calculateBearing = (
	from: { lat: number; lng: number },
	to: { lat: number; lng: number }
): number => {
	const toRadians = (deg: number) => (deg * Math.PI) / 180;
	const toDegrees = (rad: number) => (rad * 180) / Math.PI;

	const lat1 = toRadians(from.lat);
	const lat2 = toRadians(to.lat);
	const deltaLng = toRadians(to.lng - from.lng);

	const y = Math.sin(deltaLng) * Math.cos(lat2);
	const x =
		Math.cos(lat1) * Math.sin(lat2) -
		Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);

	return (toDegrees(Math.atan2(y, x)) + 360) % 360;
};

export const formatDateWithTimeFromString = (dateString: string): string =>
	dayjs
		.utc(dateString)
		.utcOffset(BRAZIL_UTC_OFFSET_MINUTES)
		.format("DD/MM/YY HH:mm");

export const formatTime = (dateString: string): string => {
	if (!dateString) {
		return "--:--";
	}

	const date = dayjs.utc(dateString);
	if (!date.isValid()) {
		return "--:--";
	}

	return date.utcOffset(BRAZIL_UTC_OFFSET_MINUTES).format("HH:mm");
};

export const matchesVehicleSearch = (
	telemetry: Telemetry,
	term: string
): boolean => {
	const normalized = term.trim().toLowerCase();
	if (!normalized) {
		return true;
	}
	return (
		telemetry.plate.toLowerCase().includes(normalized) ||
		Boolean(telemetry.driver?.name.toLowerCase().includes(normalized))
	);
};

const TRIP_END_GRACE_PERIOD_MINUTES = 30;

export const isTripEndLocationFinal = (
	index: number,
	end?: string
): boolean => {
	if (index > 0) {
		return true;
	}

	if (!end) {
		return false;
	}

	return dayjs().diff(dayjs(end), "minute") > TRIP_END_GRACE_PERIOD_MINUTES;
};

export const formatTripPeriod = (start: string, end?: string): string => {
	if (!end) {
		return formatDateWithTimeFromString(start);
	}
	return `${formatDateWithTimeFromString(start)} até ${formatDateWithTimeFromString(end)}`;
};
